import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';

interface Meeting {
  id: string;
  topic: string;
  description: string;
  start_time: string;
  duration: number;
  status: string;
  zoom_meeting_id: string;
  join_url: string;
  created_at: string;
}

// Embed interface kept for potential future use with meeting embeds display
// interface Embed {
//   id: string;
//   public_id: string;
//   is_active: boolean;
//   view_count: number;
//   join_count: number;
// }

export default function MeetingsPage() {
  const [searchParams] = useSearchParams();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatingEmbed, setCreatingEmbed] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [importing, setImporting] = useState(false);
  const [zoomAccounts, setZoomAccounts] = useState<any[]>([]);

  useEffect(() => {
    loadData();

    // Check for created meeting
    const createdId = searchParams.get('created');
    if (createdId) {
      setSuccessMessage('Meeting created successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [meetingsResponse, accountsResponse] = await Promise.all([
        api.getMeetings(),
        api.getZoomAccounts(),
      ]);
      setMeetings(meetingsResponse.meetings || []);
      setZoomAccounts(accountsResponse.accounts || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmbed = async (meetingId: string) => {
    try {
      setCreatingEmbed(meetingId);
      const response = await api.createEmbed({ meeting_id: meetingId });
      // Navigate to embed page
      window.location.href = `/embeds/${response.embed.id}`;
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create embed');
    } finally {
      setCreatingEmbed(null);
    }
  };

  const handleDeleteMeeting = async (meetingId: string) => {
    if (!confirm('Are you sure you want to delete this meeting? This will also delete any associated embeds.')) {
      return;
    }
    try {
      await api.deleteMeeting(meetingId);
      setMeetings(meetings.filter(m => m.id !== meetingId));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete meeting');
    }
  };

  const handleImportMeetings = async () => {
    if (zoomAccounts.length === 0) {
      alert('Please connect a Zoom account first');
      return;
    }

    try {
      setImporting(true);
      const response = await api.importMeetings(zoomAccounts[0].id);
      setSuccessMessage(`Imported ${response.imported} meetings (${response.skipped} skipped)`);
      loadData();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to import meetings');
    } finally {
      setImporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status: string, startTime: string) => {
    const now = new Date();
    const start = new Date(startTime);

    if (status === 'ended') {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Ended</span>;
    }
    if (status === 'live') {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-600">Live</span>;
    }
    if (start < now) {
      return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-600">Past</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-600">Upcoming</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/dashboard" className="text-2xl font-bold text-indigo-600">EmbedZoom</Link>
          <Link to="/dashboard" className="text-gray-600 hover:text-gray-800">
            Back to Dashboard
          </Link>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Meetings</h1>
          <div className="flex space-x-3">
            <button
              onClick={handleImportMeetings}
              disabled={importing || zoomAccounts.length === 0}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center space-x-2"
            >
              <span>{importing ? 'Importing...' : 'Import from Zoom'}</span>
            </button>
            <Link
              to="/meetings/create"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
            >
              <span>+ New Meeting</span>
            </Link>
          </div>
        </div>

        {successMessage && (
          <div className="bg-green-50 text-green-600 p-4 rounded-lg mb-6">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading meetings...</div>
        ) : meetings.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="text-xl font-semibold mb-2">No meetings yet</h3>
            <p className="text-gray-600 mb-6">Create your first meeting or import existing ones from Zoom.</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleImportMeetings}
                disabled={importing || zoomAccounts.length === 0}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Import from Zoom
              </button>
              <Link
                to="/meetings/create"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Create Meeting
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Meeting
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {meetings.map((meeting) => (
                  <tr key={meeting.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{meeting.topic}</div>
                      {meeting.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {meeting.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(meeting.start_time)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {meeting.duration} min
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(meeting.status, meeting.start_time)}
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => handleCreateEmbed(meeting.id)}
                        disabled={creatingEmbed === meeting.id}
                        className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {creatingEmbed === meeting.id ? 'Creating...' : 'Get Embed'}
                      </button>
                      <a
                        href={meeting.join_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 inline-block"
                      >
                        Join
                      </a>
                      <button
                        onClick={() => handleDeleteMeeting(meeting.id)}
                        className="px-3 py-1 text-red-600 text-sm hover:bg-red-50 rounded"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
